import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PhoneCall, ShieldCheck, ShieldX, Clock } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";

const RESULT_ICON = {
    confirmada: ShieldCheck,
    rechazada: ShieldX,
    sin_respuesta: Clock,
};
const RESULT_COLOR = {
    confirmada: "text-emerald-700 bg-emerald-50 border-emerald-200",
    rechazada: "text-red-700 bg-red-50 border-red-200",
    sin_respuesta: "text-amber-700 bg-amber-50 border-amber-200",
};

export default function CallBacks() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openNew, setOpenNew] = useState(false);
    const [form, setForm] = useState({
        instruccion_id: "",
        nombre: "",
        puesto: "",
        telefono: "",
        pregunta: "",
        respuesta_ok: true,
        resultado: "confirmada",
        grabacion_referencia: "",
        notas: "",
    });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/gobierno/call-backs");
            setRows(data);
        } catch (err) {
            toast.error("No se pudo cargar", {
                description: err?.response?.data?.detail,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const registrar = async () => {
        if (!form.instruccion_id || !form.nombre || !form.telefono) {
            toast.error("Faltan campos obligatorios");
            return;
        }
        try {
            const payload = {
                instruccion_id: form.instruccion_id,
                contraparte_identificada: {
                    nombre: form.nombre,
                    puesto: form.puesto || null,
                    telefono_al_que_llamo: form.telefono,
                },
                preguntas_seguridad: form.pregunta
                    ? [{ pregunta: form.pregunta, respuesta_correcta: !!form.respuesta_ok }]
                    : [],
                resultado: form.resultado,
                grabacion_referencia: form.grabacion_referencia || null,
                notas: form.notas || "",
            };
            await api.post("/gobierno/call-backs", payload);
            toast.success("Call-back registrado", {
                description: "Hash SHA-256 de evidencia generado",
            });
            setOpenNew(false);
            setForm({
                instruccion_id: "", nombre: "", puesto: "", telefono: "",
                pregunta: "", respuesta_ok: true, resultado: "confirmada",
                grabacion_referencia: "", notas: "",
            });
            load();
        } catch (err) {
            toast.error("No se pudo registrar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    return (
        <div className="p-8 lg:p-10 space-y-6 max-w-[1500px]" data-testid="callbacks-page">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Gobierno · Fase 14
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <PhoneCall className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Confirmaciones telefónicas
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Registro de call-backs para instrucciones de alta cuantía.{" "}
                        <span className="badge-gold text-[10px]">MOCK grabación</span>{" "}
                        La grabación de audio es simulada; queda referencia + hash SHA-256 firmable.
                    </p>
                </div>
                <Button onClick={() => setOpenNew(true)} data-testid="new-callback">
                    <PhoneCall className="h-3.5 w-3.5 mr-2" /> Registrar call-back
                </Button>
            </div>

            <div className="rounded-md border border-border overflow-hidden bg-card">
                <div className="overflow-x-auto">
                    <table className="trf-table" data-testid="callbacks-table">
                        <thead>
                            <tr>
                                <th>Fecha/Hora</th>
                                <th>Instrucción</th>
                                <th>Contraparte</th>
                                <th>Teléfono</th>
                                <th>Operador</th>
                                <th>Resultado</th>
                                <th>Hash evidencia</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const Icon = RESULT_ICON[r.resultado] || Clock;
                                return (
                                    <tr key={r.id} data-testid={`callback-row-${r.id}`}>
                                        <td className="tabular text-xs">{r.fecha_hora?.slice(0, 19).replace("T", " ")}</td>
                                        <td className="text-xs font-mono">{r.instruccion_id?.slice(0, 12)}…</td>
                                        <td className="text-sm">
                                            <div className="font-medium">{r.contraparte_identificada?.nombre}</div>
                                            <div className="text-muted-foreground text-[10px]">
                                                {r.contraparte_identificada?.puesto}
                                            </div>
                                        </td>
                                        <td className="tabular text-xs">
                                            {r.contraparte_identificada?.telefono_al_que_llamo}
                                        </td>
                                        <td className="text-xs">{r.delegado_operador}</td>
                                        <td>
                                            <span
                                                className={`text-[11px] uppercase tracking-widest border rounded-md px-2 py-1 inline-flex items-center gap-1 ${RESULT_COLOR[r.resultado] || ""}`}
                                            >
                                                <Icon className="h-3 w-3" />
                                                {r.resultado}
                                            </span>
                                        </td>
                                        <td className="hash-cell text-xs" title={r.hash_evidencia}>
                                            {r.hash_evidencia?.slice(0, 12)}…{r.hash_evidencia?.slice(-6)}
                                        </td>
                                    </tr>
                                );
                            })}
                            {rows.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={7} className="text-center py-10 text-muted-foreground">
                                        Sin call-backs registrados.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={openNew} onOpenChange={setOpenNew}>
                <DialogContent className="max-w-2xl" data-testid="new-callback-dialog">
                    <DialogHeader>
                        <DialogTitle>Registrar confirmación telefónica</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                ID instrucción
                            </label>
                            <Input
                                value={form.instruccion_id}
                                onChange={(e) => setForm({ ...form, instruccion_id: e.target.value })}
                                placeholder="uuid de la instrucción a confirmar"
                                data-testid="cb-instruccion-id"
                                className="font-mono text-xs"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                    Nombre contraparte
                                </label>
                                <Input
                                    value={form.nombre}
                                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                                    data-testid="cb-nombre"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                    Puesto (opcional)
                                </label>
                                <Input
                                    value={form.puesto}
                                    onChange={(e) => setForm({ ...form, puesto: e.target.value })}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                Teléfono al que se llamó
                            </label>
                            <Input
                                value={form.telefono}
                                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                                placeholder="+52 55 ..."
                                data-testid="cb-telefono"
                            />
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                Pregunta de seguridad (opcional)
                            </label>
                            <Input
                                value={form.pregunta}
                                onChange={(e) => setForm({ ...form, pregunta: e.target.value })}
                                placeholder="Ej. Último dígito RFC de la empresa"
                            />
                            <div className="flex items-center gap-2 mt-1 text-xs">
                                <input
                                    type="checkbox"
                                    checked={form.respuesta_ok}
                                    onChange={(e) => setForm({ ...form, respuesta_ok: e.target.checked })}
                                />
                                <span>Respuesta correcta</span>
                            </div>
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                Resultado
                            </label>
                            <Select
                                value={form.resultado}
                                onValueChange={(v) => setForm({ ...form, resultado: v })}
                            >
                                <SelectTrigger data-testid="cb-resultado">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="confirmada">Confirmada</SelectItem>
                                    <SelectItem value="rechazada">Rechazada</SelectItem>
                                    <SelectItem value="sin_respuesta">Sin respuesta</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                Grabación (referencia MOCK, opcional)
                            </label>
                            <Input
                                value={form.grabacion_referencia}
                                onChange={(e) => setForm({ ...form, grabacion_referencia: e.target.value })}
                                placeholder="URL o folio de grabación"
                            />
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                Notas
                            </label>
                            <Textarea
                                value={form.notas}
                                onChange={(e) => setForm({ ...form, notas: e.target.value })}
                                rows={2}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpenNew(false)}>Cancelar</Button>
                        <Button onClick={registrar} data-testid="cb-submit">Registrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
